import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth-context';

/**
 * Persistent banner shown above the main content while
 * `user.emailVerified === false`. Customer can request a fresh
 * verification email; verification itself happens on /portal/verify-email
 * (token-in-url, lands a one-shot consume call).
 *
 * The "resend" endpoint is intentionally idempotent + rate-limited
 * server-side (OCP-111) so a slightly impatient customer mashing the
 * button can't burn the mailer budget.
 */
export function EmailVerificationBanner() {
  const { user, refresh } = useAuth();
  const { t } = useTranslation('banners');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  if (!user || user.emailVerified) return null;

  async function resend() {
    if (state === 'sending') return;
    setState('sending');
    try {
      await api('/v1/portal/auth/email-verification/resend', { method: 'POST' });
      setState('sent');
      // Refresh /v1/portal/auth/me in case the user verified in another
      // tab while we were sending - best-effort, harmless.
      void refresh();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setState('error');
      } else {
        setState('error');
      }
    }
  }

  return (
    <div className="border-b border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-6 py-2 text-sm text-amber-800 dark:text-amber-300">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
        <span>
          <Trans t={t} i18nKey="verifyEmail.text" values={{ email: user.email }} components={{ strong: <strong /> }} />
        </span>
        <div className="flex items-center gap-2">
          {state === 'sent' && (
            <span className="text-xs text-amber-700">{t('verifyEmail.sent')}</span>
          )}
          {state === 'error' && (
            <span className="text-xs text-amber-700">
              {t('verifyEmail.error')}
            </span>
          )}
          <button
            type="button"
            onClick={() => void resend()}
            disabled={state === 'sending'}
            className="rounded border border-amber-300 bg-white dark:bg-gray-900 px-2 py-1 text-xs font-medium text-amber-800 dark:text-amber-300 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {state === 'sending' ? t('verifyEmail.sending') : t('verifyEmail.resend')}
          </button>
        </div>
      </div>
    </div>
  );
}
